import Link from 'next/link'
import { useRouter } from 'next/router'
import { m } from 'framer-motion'
import dayjs from 'dayjs'
import Masonry from '@mui/lab/Masonry'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import theme from 'tailwindcss/defaultTheme'
import { Media, Section } from '@/components'
import { getTranslatedField } from '@/libs/utils'
import type { ArtworkType } from '@/@types/microcms-type'

const ProjectCard = ({
    project,
    locale
}: {
    project: ArtworkType
    locale?: string
}) => {
    const {
        id,
        project_title,
        client,
        date,
        thumbnail_image,
        thumbnail_video
    } = project

    const isVideo = thumbnail_video?.video !== undefined

    return (
        <m.li
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true, margin: '-5%' }}
            key={id}
            className="list-none"
        >
            <Link
                href={`/project/${id}`}
                scroll={false}
                className="lg:mx-[1.5%] grid gap-2 lg:relative group relative md:[&_.media]:hover:brightness-50 [&_.media]:duration-[450ms] after:content-[''] after:w-full after:h-full after:block after:absolute after:z-1 after:bg-v-navy after:opacity-0 lg:after:hover:opacity-100 after:transition-opacity after:duration-[450ms] after:mix-blend-darken"
            >
                <Media
                    media={isVideo ? thumbnail_video : thumbnail_image}
                    altText={getTranslatedField({
                        field: project_title,
                        locale
                    })}
                    sizes="(min-width: 768px) 33vw, 50vw"
                    className="media w-full h-auto object-contain"
                />
                <div className="grid gap-y-1 lg:gap-y-2 py-2 text-v-gray-dark lg:text-v-white [&_*]:text-v-xs md:[&_*]:text-v-md lg:[&_*]:text-[1.8rem] [&_*]:leading-v-heading lg:absolute lg:top-0 lg:left-0 lg:w-full lg:p-[4%] only-sm:[&_span]:hidden z-[2] lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-[300ms]">
                    <h3>
                        <span>&#8208;</span>
                        {client &&
                            getTranslatedField({ field: client, locale })}
                    </h3>
                    <h2>
                        <span>&#8208;</span>
                        {getTranslatedField({ field: project_title, locale })}
                    </h2>
                    {date && (
                        <time dateTime={date}>
                            <span>&#8208;</span>
                            {dayjs(date).format('YYYY')}
                        </time>
                    )}
                </div>
            </Link>
        </m.li>
    )
}

export const ProjectByPerson = ({ projects }: { projects: ArtworkType[] }) => {
    const locale = useRouter().locale

    const screenSM = Number(theme.screens.sm.replace('px', ''))
    const screenMD = Number(theme.screens.md.replace('px', ''))
    const screenLG = Number(theme.screens.lg.replace('px', ''))

    const muiTheme = createTheme({
        breakpoints: {
            values: {
                ...createTheme().breakpoints.values,
                sm: screenSM,
                md: screenMD,
                lg: screenLG
            }
        }
    })

    return (
        <ThemeProvider theme={muiTheme}>
            <Section width="wide" className="my-20 md:mt-[20vh]">
                <Masonry
                    columns={{ xs: 1, sm: 1, md: 2, lg: 3 }}
                    spacing={{ xs: 4, sm: 4, md: 4, lg: 1 }}
                    className="!m-0"
                >
                    {projects.map((project) => (
                        <ProjectCard
                            key={project.id}
                            locale={locale}
                            project={project}
                        />
                    ))}
                </Masonry>
            </Section>
        </ThemeProvider>
    )
}
